import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';

import { API_URL as API, SOCKET_URL } from '../config/api';
import { getSharedAuth, setSharedAuth, clearSharedAuth } from '../utils/authStorage';

const AuthContext = createContext();

// Safe helper to parse JWT payload without external heavy dependencies
const parseJwt = (token) => {
    try {
        if (!token || typeof token !== 'string') return null;
        const base64Url = token.split('.')[1];
        if (!base64Url) return null;
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            atob(base64)
                .split('')
                .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
                .join('')
        );
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [role, setRole] = useState(null);
    const [tenantId, setTenantId] = useState(null);
    const [socket, setSocket] = useState(null);
    const [loading, setLoading] = useState(true);
    const socketRef = useRef(null);

    const logout = useCallback((reason) => {
        clearSharedAuth();
        setUser(null);
        setRole(null);
        setTenantId(null);
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
        setSocket(null);

        if (reason) {
            toast.error(reason, { id: 'session-expired' });
        } else {
            toast.success('Logged out successfully');
        }
    }, []);

    const setupSocket = useCallback((tid) => {
        if (socketRef.current) {
            socketRef.current.disconnect();
        }

        const newSocket = io(SOCKET_URL, {
            transports: ['websocket', 'polling'],
        });

        newSocket.on('connect', () => {
            newSocket.emit('joinTenant', tid);
        });

        newSocket.on('connect_error', (err) => {
            console.error('Socket connection error:', err);
        });

        socketRef.current = newSocket;
        setSocket(newSocket);
    }, []);

    // Proactive check if session token is still valid
    const checkSessionValidity = useCallback(() => {
        const { token } = getSharedAuth();
        if (!token) return false;

        const decoded = parseJwt(token);
        if (!decoded || !decoded.exp) {
            return true; // Token without explicit exp
        }

        const currentTime = Math.floor(Date.now() / 1000);
        if (decoded.exp <= currentTime) {
            logout('Your session has expired. Please sign in again.');
            return false;
        }
        return true;
    }, [logout]);

    // Setup global Axios Interceptors for Session & Authorization Management
    useEffect(() => {
        const requestInterceptor = axios.interceptors.request.use(
            (config) => {
                const { token } = getSharedAuth();
                if (token) {
                    config.headers['x-auth-token'] = token;
                    config.headers['Authorization'] = `Bearer ${token}`;
                }
                return config;
            },
            (error) => Promise.reject(error)
        );

        const responseInterceptor = axios.interceptors.response.use(
            (response) => response,
            (error) => {
                const originalRequest = error.config;
                // If 401 and request is not login/register endpoint, auto logout
                if (
                    error.response &&
                    error.response.status === 401 &&
                    originalRequest &&
                    !originalRequest.url.includes('/auth/login') &&
                    !originalRequest.url.includes('/auth/register-tenant')
                ) {
                    const { token } = getSharedAuth();
                    if (token) {
                        logout('Session expired or unauthorized. Please sign in again.');
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.request.eject(requestInterceptor);
            axios.interceptors.response.eject(responseInterceptor);
        };
    }, [logout]);

    // Initialize Auth state from LocalStorage & Shared Cookie on mount
    useEffect(() => {
        const { token, user: storedUser } = getSharedAuth();

        if (token && storedUser) {
            const isValid = checkSessionValidity();
            if (isValid) {
                try {
                    setUser(storedUser);
                    setRole(storedUser.role);
                    setTenantId(storedUser.tenantId);

                    if (storedUser.tenantId) {
                        setupSocket(storedUser.tenantId);
                    }
                } catch (err) {
                    console.error('Auth initialization error:', err);
                    logout();
                }
            }
        }
        setLoading(false);
    }, [checkSessionValidity, logout, setupSocket]);

    // Periodic & Tab focus session expiry checks
    useEffect(() => {
        const interval = setInterval(() => {
            const { token } = getSharedAuth();
            if (token) {
                checkSessionValidity();
            }
        }, 30000); // Check every 30 seconds

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                const { token } = getSharedAuth();
                if (token) {
                    checkSessionValidity();
                }
            }
        };

        window.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleVisibilityChange);

        return () => {
            clearInterval(interval);
            window.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleVisibilityChange);
        };
    }, [checkSessionValidity]);

    const setSessionDirect = useCallback((token, loggedInUser) => {
        const decoded = parseJwt(token);
        if (decoded && decoded.exp) {
            localStorage.setItem('token_exp', decoded.exp);
        }

        setSharedAuth(token, loggedInUser);
        setUser(loggedInUser);
        setRole(loggedInUser.role);
        setTenantId(loggedInUser.tenantId);

        if (loggedInUser.tenantId) {
            setupSocket(loggedInUser.tenantId);
        }
    }, [setupSocket]);

    const login = async (email, password) => {
        try {
            const res = await axios.post(`${API}/auth/login`, { email, password });
            const { token, user: loggedInUser } = res.data;

            setSessionDirect(token, loggedInUser);

            toast.success(`Welcome back, ${loggedInUser.name}!`);
            return { success: true, user: loggedInUser, token };
        } catch (err) {
            console.error('Login error:', err);
            const errorMsg =
                err.response?.data?.error ||
                (err.response?.data?.errors && err.response?.data?.errors[0]?.msg) ||
                (err.message === 'Network Error' ? 'Unable to connect to server. Please check your network connection.' : 'Login failed');
            
            toast.error(errorMsg);
            return {
                success: false,
                error: errorMsg,
                errors: err.response?.data?.errors || []
            };
        }
    };

    const registerTenant = async (data) => {
        try {
            const res = await axios.post(`${API}/auth/register-tenant`, data);
            toast.success(res.data?.message || 'Application submitted for Super Admin review!');
            return { success: true, data: res.data };
        } catch (err) {
            console.error('Registration error:', err);
            const errorMsg =
                err.response?.data?.error ||
                (err.response?.data?.errors && err.response?.data?.errors[0]?.msg) ||
                (err.message === 'Network Error' ? 'Unable to connect to server. Please check your network connection.' : 'Registration failed');
            
            toast.error(errorMsg);
            return {
                success: false,
                error: errorMsg,
                errors: err.response?.data?.errors || [],
                pendingApproval: err.response?.data?.pendingApproval
            };
        }
    };

    const googleLogin = async (googlePayload) => {
        try {
            const res = await axios.post(`${API}/auth/google-login`, googlePayload);
            const { token, user: userData } = res.data;
            if (token && userData) {
                setSharedAuth(token, userData);
                setUser(userData);
                setRole(userData.role);
                setTenantId(userData.tenantId);
                toast.success(`Welcome back, ${userData.name}!`);
                return { success: true, user: userData, token };
            }
            return { success: false, data: res.data };
        } catch (err) {
            console.error('Google login error:', err);
            const errorMsg =
                err.response?.data?.error ||
                (err.message === 'Network Error' ? 'Unable to connect to server.' : 'Google sign-in failed');
            return {
                success: false,
                error: errorMsg,
                pendingApproval: err.response?.data?.pendingApproval,
                isNewUser: err.response?.data?.isNewUser,
                email: err.response?.data?.email,
                name: err.response?.data?.name
            };
        }
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                role,
                tenantId,
                socket,
                loading,
                login,
                googleLogin,
                logout,
                setSessionDirect,
                registerTenant,
                checkSessionValidity,
                isAuthenticated: !!user && !!getSharedAuth().token
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
